{/*
  * DspSKAdNetworkPage.tsx
  * https://dash.applovin.com/documentation/dsp/skadnetwork
  * Documentation > Demand Partners > DSPs > SKAdNetwork
  */}
import React, { FunctionComponent } from 'react';
import { AlertMessage, AlertMessageType, DocsPageModule, LabelFontFamily } from 'UI-Dash-Commons-UI';

type DspSKAdNetworkPageProps = {
    linkTargetTopPosition?: number;
}

const DspSKAdNetworkPage: FunctionComponent<DspSKAdNetworkPageProps> = ( { linkTargetTopPosition } ) =>
{
    return (
        <>
            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'SKAdNetwork (SKAN)'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_1}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={`AppLovin SDK fully supports Apple’s `} />
                <DocsPageModule.DocsPageContentAnchor
                    text={'SKAdNetwork attribution'}
                    to={'https://developer.apple.com/documentation/storekit/skadnetwork'}
                    newWindow={true} />
                <DocsPageModule.DocsPageContentText text={` by allowing DSPs to submit signed clicks to the SKAdNetwork API. Please review `} />
                <DocsPageModule.DocsPageContentAnchor
                    text={'the bid request/response documentation'}
                    to={'/documentation/mediation/auctions/dsps/ortb-spec/introduction'}
                    newWindow={false} />
                <DocsPageModule.DocsPageContentText text={` for integration details.`} />
            </DocsPageModule.DocsPageContent>

            <AlertMessage type={AlertMessageType.INFO}>
                <DocsPageModule.DocsPageContentText text={'Note: '} fontFamily={LabelFontFamily.FONT_FAMILY_DEMI} />
                <DocsPageModule.DocsPageContentText text={'SKANetwork attribution requires a SKANetwork ID which you can obtain by registering with Apple.'} />
            </AlertMessage>

            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'How It Works'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>

            <DocsPageModule.DocsPageOrderedList>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'The AppLovin SDK retrieves the '} />
                    <DocsPageModule.DocsPageContentCode children={'SKAdNetworkItems'}/>
                    <DocsPageModule.DocsPageContentText text={' from the source app’s '}/>
                    <DocsPageModule.DocsPageContentCode children={'Info.plist'}/>
                    <DocsPageModule.DocsPageContentText text={'.'}/>
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'AppLovin Exchange includes the '}/>
                    <DocsPageModule.DocsPageContentCode children={'imp.ext.skadn'}/>
                    <DocsPageModule.DocsPageContentText text={' object in the bid request sent to those demand sources that are included in the source app’s '}/>
                    <DocsPageModule.DocsPageContentCode children={'Info.plist'}/>
                    <DocsPageModule.DocsPageContentText text={'.'}/>
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'If the winning ad requires SKAdNetwork support then the bid response includes '}/>
                    <DocsPageModule.DocsPageContentCode children={'seatbid.bid.ext.skadn'}/>
                    <DocsPageModule.DocsPageContentText text={'.'}/>
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'If the user clicks on the ad, Apple considers the source app for install attribution using SKAdNetwork.'}/>
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'If SKAdNetwork determines that the click from the DSP’s ad led to the install, Apple sends the postback directly to the DSP.'}/>
                </DocsPageModule.DocsPageBulletPoint>
            </DocsPageModule.DocsPageOrderedList>

            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Creative Type Specification'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>
            <DocsPageModule.DocsPageContent
                text={'The following recommendations show you how to properly handle clicks, according to each of the AppLovin-supported Creative Types:'} />
            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'VAST'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_3}/>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'When the SKAdNetwork (skadn object) is included in the bid response the '} />
                <DocsPageModule.DocsPageContentCode children={'<ClickThrough>'} />
                <DocsPageModule.DocsPageContentText text={' node is ignored. Use the '} />
                <DocsPageModule.DocsPageContentCode children={'<ClickTracking>'} />
                <DocsPageModule.DocsPageContentText text={' node if click tracking is needed.'} />
            </DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'HTML'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_3}/>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'AppLovin accepts click-tracking URLs via the Bid Response '}/>
                <DocsPageModule.DocsPageContentCode children={'seatbid.bid.ext.clicktrackers'} />
                <DocsPageModule.DocsPageContentText
                    text={'array. This is supported on all SDK versions that support SKAN, but not on versions earlier than that. '
                        + 'Only include this object when AppLovin includes SKAN parameters in the bid request. '
                        + 'When the user clicks an ad, the AppLovin SDK renders the StoreKit, asynchronously hits each URL, and follows redirects (response codes in the 300s).'} />
            </DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'MRAID'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_3}/>
            <DocsPageModule.DocsPageContent text={'There is nothing special you need to do here; the AppLovin SDK handles clicks properly.'} />

            <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Considerations'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_1}/>
            <DocsPageModule.DocsPageUnorderedList>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'Custom Product Page ID (available as of SDK 11.1.1) via '} />
                    <DocsPageModule.DocsPageContentCode children={'ext.skadn.productpage'} />
                    <DocsPageModule.DocsPageContentText text={'.'} />
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'SKOverlay support added (available as of SDK 10.0.0). Note: Apple does not provide a callback for clicks on the overlay so AL and DSP click tracking does not work.'} />
                </DocsPageModule.DocsPageBulletPoint>
            </DocsPageModule.DocsPageUnorderedList>

        </>
    )
}

export default DspSKAdNetworkPage;
